import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  NgbDate,
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { baseDatepickerValidator } from '../../validators/base-datepicker.validator';
import { AbstractFormComponent } from '../_abstract-classes/abstract-form-component';
import { BaseCustomAdapter, BaseCustomDateParserFormatter } from './base-datepicker.adapter';
import { BaseCustomDatepickerI18n } from './base-datepicker.i18n';
import { BaseDateService, BASE_DATE_SERVICE_FORMAT } from '../../services/base-date.service';
import { DEFAULT_MIN_DATE, DEFAULT_MAX_DATE } from '../../constants/base-datepicker.const';

@Component({
  selector: 'base-datepicker',
  templateUrl: './base-datepicker.template.html',
  providers: [
    { provide: NgbDateAdapter, useClass: BaseCustomAdapter },
    { provide: NgbDateParserFormatter, useClass: BaseCustomDateParserFormatter },
    { provide: NgbDatepickerI18n, useClass: BaseCustomDatepickerI18n },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: BaseDatepickerComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useValue: baseDatepickerValidator,
      multi: true,
    },
  ],
})
export class BaseDatepickerComponent
  extends AbstractFormComponent
  implements ControlValueAccessor, DoCheck, AfterViewInit
{
  @ViewChild('input', { static: false })
  private input!: ElementRef<HTMLInputElement>;
  model: NgbDateStruct | null | undefined | string = null;
  @Input() isInvalid = false;
  @Input() placeholder = 'dd.mm.yyyy';
  @Input() disabled = false;
  @Input() label = '';
  @Input() unlimitedCheck = false;
  @Output() unlimitedChecked = new EventEmitter();
  @Output() selectedDate = new EventEmitter();
  @Input() tabIndex = 0;
  @Input() errorText = '';
  @Input() isRequired = false;
  @Input() isPreview = false;
  @Input() minDate: NgbDateStruct = DEFAULT_MIN_DATE;
  @Input() maxDate: NgbDateStruct = DEFAULT_MAX_DATE;
  @Input() placement = 'auto';
  @Input() isFilter = false;
  @Input() readonly = false;
  @Input() disabledDatesSet: Set<string> = new Set<string>();
  @Input() hideSelectedRange = false;

  constructor(private dateAdapter: NgbDateAdapter<string>, protected renderer: Renderer2) {
    super(renderer);
  }

  ngDoCheck(): void {
    this.setNgClass();
    this.clearInput();
  }

  ngAfterViewInit(): void {
    this.setNgClass();
  }

  onChange = (date?: string | null) => {
    this.model = date;
  };

  onTouched = () => {};

  isDisabled = (date: NgbDate) => {
    return this.disabledDatesSet.has(this.ngbDateToString(date));
  };

  ngbDateToString(ngbDate: NgbDate): string {
    const date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day);
    return BaseDateService.format(date, BASE_DATE_SERVICE_FORMAT);
  }

  writeValue(value: any): void {
    this.model = value || null;
  }

  registerOnChange(fn: (date: string | undefined | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDateSelect(value: NgbDateStruct): void {
    this.onChange(this.dateAdapter.toModel(value));
  }

  onDateChange(event: any): void {
    this.onChange(BaseDateService.toServerFormatString(event.target.value));
  }

  onUnlimitedClick(event: any): void {
    this.unlimitedChecked.emit(event.target.checked);
  }

  private clearInput() {
    if (this.hideSelectedRange && this.input) {
      if (this.input.nativeElement.value !== '') {
        this.renderer.setProperty(this.input.nativeElement, 'value', '');
      }
    }
  }
}
